import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import { ArrowLeft, UploadSimple, FileArrowDown, PencilSimple, Trash, CheckCircle, Warning, Eye, ArrowsClockwise, Archive, CopySimple, ArrowRight, Users } from "@phosphor-icons/react";

const EUR = (n) => (typeof n === "number" ? n.toLocaleString("es-ES", { style: "currency", currency: "EUR" }) : "—");

export default function PortalAsesorCliente() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const [cliente, setCliente] = useState(null);
    const [empresas, setEmpresas] = useState([]);
    const [selectedEmpresa, setSelectedEmpresa] = useState("");
    const [showNewEmp, setShowNewEmp] = useState(false);
    const [empNombre, setEmpNombre] = useState("");
    const [empCif, setEmpCif] = useState("");
    const [showUploadPicker, setShowUploadPicker] = useState(false);
    const [uploadEmpresa, setUploadEmpresa] = useState("");
    const [uploadTipoHint, setUploadTipoHint] = useState("auto");
    const [pendingFile, setPendingFile] = useState(null);
    const [facturas, setFacturas] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [ejercicios, setEjercicios] = useState([]);
    const [filterEjercicio, setFilterEjercicio] = useState("");
    const [filterTipo, setFilterTipo] = useState("");
    const [onlyWarnings, setOnlyWarnings] = useState(false);
    const [onlyDuplicates, setOnlyDuplicates] = useState(false);
    const [skipDupsInExcel, setSkipDupsInExcel] = useState(false);
    const [totalDuplicates, setTotalDuplicates] = useState(0);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewName, setPreviewName] = useState("");
    const [reprocJob, setReprocJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [uploadJob, setUploadJob] = useState(null);
    const [resumen, setResumen] = useState([]);
    const [editing, setEditing] = useState(null);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [moveTargetEmp, setMoveTargetEmp] = useState("");
    const [moveTargetCliente, setMoveTargetCliente] = useState("");
    const [otrosClientes, setOtrosClientes] = useState([]);
    const [moving, setMoving] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [showZipModal, setShowZipModal] = useState(false);
    const [zipGroupByCounterpart, setZipGroupByCounterpart] = useState(false);
    const [zipEmailDest, setZipEmailDest] = useState("");
    const [zipBusy, setZipBusy] = useState(false);
    const [filterTrimestre, setFilterTrimestre] = useState("");
    const [filterContraparte, setFilterContraparte] = useState("");
    const [sources, setSources] = useState([]);
    const [filterSource, setFilterSource] = useState("");
    const [filterIntracom, setFilterIntracom] = useState(false);
    const [filterInversion, setFilterInversion] = useState(false);
    const [tiposGasto, setTiposGasto] = useState([]);
    const [filterTipoGasto, setFilterTipoGasto] = useState("");
    const [sortBy, setSortBy] = useState("fecha");
    const [sortDir, setSortDir] = useState("asc");
    const fileRef = useRef(null);

    function toggleSort(field) {
        if (sortBy === field) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    }

    function sortArrow(field) {
        if (sortBy !== field) return "";
        return sortDir === "asc" ? " ↑" : " ↓";
    }

    async function loadAll() {
        setLoading(true); setError("");
        try {
            const [c, e, f, r] = await Promise.all([
                api.get(`/asesor/clientes/${id}`),
                api.get(`/asesor/clientes/${id}/empresas`),
                api.get(`/asesor/clientes/${id}/facturas${paramsQ()}`),
                api.get(`/asesor/clientes/${id}/resumen${selectedEmpresa ? `?empresa_id=${selectedEmpresa}` : ""}`),
            ]);
            setCliente(c.data);
            setEmpresas(e.data.items || []);
            setFacturas(f.data.items || []);
            setEjercicios(f.data.ejercicios || []);
            setTotalDuplicates(f.data.duplicates || 0);
            setSources(f.data.sources || []);
            setResumen(r.data.items || []);
        } catch (e) {
            setError(formatApiError(e, "Error al cargar cliente"));
        } finally {
            setLoading(false);
        }
    }
    function paramsQ() {
        const p = [];
        if (selectedEmpresa) p.push(`empresa_id=${selectedEmpresa}`);
        if (filterEjercicio) p.push(`ejercicio=${filterEjercicio}`);
        if (filterTipo) p.push(`tipo=${filterTipo}`);
        return p.length ? `?${p.join("&")}` : "";
    }
    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id, filterEjercicio, filterTipo, selectedEmpresa]);

    // Cargar catálogo de tipos de gasto (PGC) una sola vez
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/asesor/tipos-gasto");
                setTiposGasto(data.items || []);
            } catch (e) { /* ignore */ }
        })();
    }, []);

    async function createTipoGasto(codigo, nombre) {
        const c = (codigo || "").trim();
        const n = (nombre || "").trim();
        if (!c || !n) return null;
        try {
            const { data } = await api.post("/asesor/tipos-gasto", { codigo: c, nombre: n });
            setTiposGasto((prev) => [...prev, data].sort((a, b) => a.codigo.localeCompare(b.codigo)));
            return data;
        } catch (e) {
            alert(formatApiError(e, "Error al crear la cuenta"));
            return null;
        }
    }

    // Deep-link desde el Directorio: ?empresa_id=X&contraparte=CIF|NOMBRE
    useEffect(() => {
        const emp = searchParams.get("empresa_id");
        const cp = searchParams.get("contraparte");
        if (emp) setSelectedEmpresa(emp);
        if (cp) setFilterContraparte(cp);
        // eslint-disable-next-line
    }, []);

    async function createEmpresa(e) {
        e.preventDefault();
        if (!empNombre.trim()) return;
        try {
            const { data } = await api.post(`/asesor/clientes/${id}/empresas`, { nombre: empNombre, cif: empCif });
            setShowNewEmp(false); setEmpNombre(""); setEmpCif("");
            await loadAll();
            setSelectedEmpresa(data.id);
        } catch (err) {
            alert(formatApiError(err, "Error al crear empresa"));
        }
    }

    async function deleteEmpresa(emp) {
        if (!confirm(`¿Eliminar "${emp.nombre}" y todas sus facturas? Esta acción no se puede deshacer.`)) return;
        try {
            await api.delete(`/asesor/empresas/${emp.id}`);
            if (selectedEmpresa === emp.id) setSelectedEmpresa("");
            await loadAll();
        } catch (err) {
            alert(formatApiError(err, "Error al eliminar empresa"));
        }
    }

    function onSelectFile(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.name.toLowerCase().endsWith(".zip")) { alert("El archivo debe ser un ZIP"); return; }
        if (empresas.length === 0) {
            alert("Antes de subir un ZIP, crea al menos una empresa en este cliente.");
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        setPendingFile(f);
        // Preseleccionar la empresa filtrada actual si la hay
        setUploadEmpresa(selectedEmpresa || empresas[0]?.id || "");
        setShowUploadPicker(true);
    }

    async function confirmUpload() {
        if (!pendingFile || !uploadEmpresa) return;
        const fd = new FormData();
        fd.append("file", pendingFile);
        fd.append("empresa_id", uploadEmpresa);
        fd.append("tipo_hint", uploadTipoHint);
        setShowUploadPicker(false);
        setUploadJob({ status: "uploading" });
        try {
            const { data } = await api.post(`/asesor/clientes/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setUploadJob({ upload_id: data.upload_id, status: "queued", total: 0, processed: 0, failed: 0, empresa: data.empresa });
        } catch (err) {
            setError(formatApiError(err, "Error al subir ZIP"));
            setUploadJob(null);
        } finally {
            setPendingFile(null);
            if (fileRef.current) fileRef.current.value = "";
        }
    }

    // Polling del upload
    const uploadUid = uploadJob?.upload_id || uploadJob?.id;
    useEffect(() => {
        const inactive = ["done", "crashed", "interrupted", "cancelled"];
        if (!uploadUid || inactive.includes(uploadJob.status)) return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/asesor/uploads/${uploadUid}`);
                setUploadJob({ ...data, upload_id: data.upload_id || data.id });
                if (inactive.includes(data.status)) {
                    clearInterval(t);
                    await loadAll();
                }
            } catch {}
        }, 1500);
        return () => clearInterval(t);
    }, [uploadUid, uploadJob?.status]); // eslint-disable-line

    async function saveFactura(f) {
        try {
            await api.put(`/asesor/facturas/${f.id}`, f);
            setEditing(null);
            await loadAll();
        } catch (e) {
            alert(formatApiError(e, "Error al guardar"));
        }
    }

    async function deleteFactura(f) {
        if (!confirm(`¿Eliminar la factura ${f.numero || "(sin nº)"}?`)) return;
        try {
            await api.delete(`/asesor/facturas/${f.id}`);
            await loadAll();
        } catch (e) {
            alert(formatApiError(e, "Error al eliminar"));
        }
    }

    async function openPreview(f) {
        try {
            const token = localStorage.getItem("gf347_token");
            const res = await fetch(`${API}/asesor/facturas/${f.id}/file`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) {
                const t = await res.text();
                alert(`No se puede previsualizar este archivo.\n${t.slice(0, 200)}`);
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
            setPreviewName(f.archivo_original || "factura");
        } catch (e) {
            alert("No se pudo abrir el archivo.");
        }
    }

    function closePreview() {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setPreviewName("");
    }

    async function cancelUpload() {
        const uid = uploadJob?.id || uploadJob?.upload_id;
        if (!uid) return;
        if (!confirm("¿Cancelar el proceso? Las facturas ya procesadas se quedarán guardadas.")) return;
        try {
            await api.post(`/asesor/uploads/${uid}/cancel`);
            const { data } = await api.get(`/asesor/uploads/${uid}`);
            setUploadJob(data);
            await loadAll();
        } catch (e) {
            alert(formatApiError(e, "Error al cancelar"));
        }
    }

    async function retryFailed() {
        const uid = uploadJob?.id || uploadJob?.upload_id;
        if (!uid) return;
        const n = uploadJob?.failed || 0;
        if (!confirm(`¿Reintentar los ${n} archivos fallidos? Se procesarán sólo esos.`)) return;
        try {
            const { data } = await api.post(`/asesor/uploads/${uid}/retry-failed`);
            setUploadJob({ upload_id: uid, status: "queued", total: 0, processed: 0, failed: 0, failed_files: [] });
        } catch (e) {
            alert(formatApiError(e, "Error al reintentar"));
        }
    }

    async function deleteDuplicates() {
        if (!confirm("¿Borrar todas las facturas duplicadas dejando sólo una por grupo? Se conserva la más antigua.")) return;
        try {
            const body = {};
            const qs = [];
            if (selectedEmpresa) qs.push(`empresa_id=${selectedEmpresa}`);
            if (filterEjercicio) qs.push(`ejercicio=${filterEjercicio}`);
            const params = qs.length ? `?${qs.join("&")}` : "";
            const { data } = await api.post(`/asesor/clientes/${id}/delete-duplicates${params}`, body);
            alert(data.deleted > 0
                ? `Se borraron ${data.deleted} facturas duplicadas (de ${data.groups} grupos).`
                : (data.message || "No hay duplicados."));
            await loadAll();
        } catch (e) {
            alert(formatApiError(e, "Error al borrar duplicados"));
        }
    }

    async function swapTipos() {
        const partes = [];
        if (selectedEmpresa) partes.push(`empresa filtrada`);
        if (filterEjercicio) partes.push(`ejercicio ${filterEjercicio}`);
        if (filterTipo) partes.push(`sólo ${filterTipo}s`);
        const label = partes.length ? partes.join(", ") : "TODAS las facturas del cliente";
        if (!confirm(`¿Invertir emitidas↔recibidas (y sus emisor/receptor) en ${label}?\n\nÚsalo cuando la IA clasificó al revés un lote entero. Esta acción se puede deshacer volviendo a pulsar el botón.`)) return;
        try {
            const qs = [];
            if (selectedEmpresa) qs.push(`empresa_id=${selectedEmpresa}`);
            if (filterEjercicio) qs.push(`ejercicio=${filterEjercicio}`);
            if (filterTipo) qs.push(`only_tipo=${filterTipo}`);
            const params = qs.length ? `?${qs.join("&")}` : "";
            const { data } = await api.post(`/asesor/clientes/${id}/swap-tipos${params}`);
            alert(data.swapped > 0
                ? `Se invirtieron ${data.swapped} facturas.`
                : (data.message || "Nada que invertir."));
            await loadAll();
        } catch (e) {
            alert(formatApiError(e, "Error al invertir tipos"));
        }
    }

    async function confirmSelected(newState) {
        if (selectedIds.size === 0) return;
        try {
            const { data } = await api.post(`/asesor/facturas/confirm-bulk`, {
                factura_ids: Array.from(selectedIds),
                confirmed: newState,
            });
            setSelectedIds(new Set());
            await loadAll();
        } catch (e) {
            alert(formatApiError(e, "Error al marcar confirmadas"));
        }
    }

    async function swapTiposSelected() {
        if (selectedIds.size === 0) return;
        if (!confirm(`¿Invertir emitida↔recibida en las ${selectedIds.size} facturas seleccionadas?\n\nSe intercambian también emisor y receptor. La acción se puede deshacer volviendo a pulsar el botón sobre las mismas facturas.`)) return;
        try {
            const { data } = await api.post(`/asesor/clientes/${id}/swap-tipos`, {
                factura_ids: Array.from(selectedIds),
            });
            alert(data.swapped > 0
                ? `Se invirtieron ${data.swapped} facturas seleccionadas.`
                : (data.message || "Nada que invertir."));
            setSelectedIds(new Set());
            await loadAll();
        } catch (e) {
            alert(formatApiError(e, "Error al invertir tipos"));
        }
    }

    async function openMoveModal() {
        if (selectedIds.size === 0) {
            alert("Primero marca con las casillas ☑ (columna izquierda) las facturas que quieres mover.");
            return;
        }
        // Cargar OTROS clientes del asesor (excluyendo el actual)
        try {
            const { data } = await api.get(`/asesor/clientes`);
            const otros = (data.items || []).filter((c) => c.id !== id);
            setOtrosClientes(otros);
        } catch {
            setOtrosClientes([]);
        }
        // Destino por defecto: primera empresa distinta a la actual dentro del mismo cliente,
        // o si solo hay 1 empresa, el primer OTRO cliente.
        const otraEmpresa = empresas.find((e) => e.id !== selectedEmpresa) || null;
        if (otraEmpresa) {
            setMoveTargetCliente(`empresa:${otraEmpresa.id}`);
        } else {
            setMoveTargetCliente("");
        }
        setShowMoveModal(true);
    }

    async function moverFacturasSelected() {
        if (selectedIds.size === 0 || !moveTargetCliente) return;
        setMoving(true);
        try {
            let data;
            if (moveTargetCliente.startsWith("empresa:")) {
                const empId = moveTargetCliente.replace("empresa:", "");
                const res = await api.post(`/asesor/clientes/${id}/mover-facturas`, {
                    factura_ids: Array.from(selectedIds),
                    empresa_destino_id: empId,
                });
                data = res.data;
                if (data.moved > 0) {
                    alert(`Se movieron ${data.moved} facturas a "${data.empresa_destino}".\n\nSe recalcularon los avisos con el CIF de la empresa destino.`);
                } else {
                    alert(data.message || "No se movió ninguna factura (ya estaban en esa empresa).");
                }
            } else if (moveTargetCliente.startsWith("cliente:")) {
                const clienteId = moveTargetCliente.replace("cliente:", "");
                const res = await api.post(`/asesor/mover-facturas-cliente`, {
                    factura_ids: Array.from(selectedIds),
                    cliente_destino_id: clienteId,
                });
                data = res.data;
                if (data.moved > 0) {
                    alert(`Se movieron ${data.moved} facturas al cliente "${data.cliente_destino}" (empresa "${data.empresa_destino}").\n\nSe recalcularon los avisos con el CIF de destino.`);
                } else {
                    alert(data.message || "No se movió ninguna factura.");
                }
            }
            setSelectedIds(new Set());
            setShowMoveModal(false);
            setMoveTargetCliente("");
            await loadAll();
        } catch (e) {
            alert(formatApiError(e, "Error al mover facturas"));
        } finally {
            setMoving(false);
        }
    }

    function toggleSelect(id) {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    function toggleSelectAll(ids) {
        setSelectedIds((prev) => {
            const allSelected = ids.every((i) => prev.has(i));
            if (allSelected) {
                const next = new Set(prev);
                ids.forEach((i) => next.delete(i));
                return next;
            }
            return new Set([...prev, ...ids]);
        });
    }

    async function reprocessOne(f) {
        if (!confirm(`¿Reprocesar la factura ${f.numero || "(sin nº)"} con la IA?\nSe sobreescribirán los campos con la nueva extracción.`)) return;
        try {
            await api.post(`/asesor/facturas/${f.id}/reprocess`);
            await loadAll();
            setEditing(null);
        } catch (e) {
            alert(formatApiError(e, "Error al reprocesar"));
        }
    }

    async function reprocessBulk(opts) {
        const ids = opts.ids;
        // Feedback inmediato — sin confirm() del navegador (Safari privado a veces lo bloquea)
        setReprocJob({ job_id: null, total: ids?.length || 0, processed: 0, updated: 0, failed: 0, status: "queued" });
        try {
            const body = { only_warnings: !!opts.only_warnings };
            if (ids && ids.length) body.ids = ids;
            if (!ids) {
                if (selectedEmpresa) body.empresa_id = selectedEmpresa;
                if (filterEjercicio) body.ejercicio = parseInt(filterEjercicio, 10);
            }
            console.log("[reprocessBulk] POST /asesor/clientes/" + id + "/reprocess", body);
            const { data } = await api.post(`/asesor/clientes/${id}/reprocess`, body);
            console.log("[reprocessBulk] response", data);
            if (!data.queued) {
                setReprocJob({ status: "blocked", message: data.message || "No hay nada que reprocesar." });
                return;
            }
            setReprocJob({ job_id: data.job_id, total: data.total, processed: 0, updated: 0, failed: 0, status: "running", skipped_no_file: data.skipped_no_file });
            if (ids) setSelectedIds(new Set());
        } catch (e) {
            console.error("[reprocessBulk] error", e);
            setReprocJob({ status: "blocked", message: formatApiError(e, "Error al lanzar reprocesado") });
        }
    }

    // Polling reproc
    const reprocUid = reprocJob?.job_id || reprocJob?.id;
    useEffect(() => {
        if (!reprocUid || reprocJob.status === "done") return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/asesor/reproc-jobs/${reprocUid}`);
                setReprocJob({ ...data, job_id: data.job_id || data.id });
                if (data.status === "done") {
                    clearInterval(t);
                    await loadAll();
                }
            } catch {}
        }, 3000);
        return () => clearInterval(t);
    }, [reprocUid, reprocJob?.status]); // eslint-disable-line

    function downloadReorganized() {
        const token = localStorage.getItem("gf347_token");
        const qs = [];
        if (filterEjercicio) qs.push(`ejercicio=${filterEjercicio}`);
        if (selectedEmpresa) qs.push(`empresa_id=${selectedEmpresa}`);
        if (zipGroupByCounterpart) qs.push(`group_by_counterpart=true`);
        const params = qs.length ? `?${qs.join("&")}` : "";
        const url = `${API}/asesor/clientes/${id}/reorganized.zip${params}`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(async (r) => {
                if (!r.ok) {
                    const t = await r.text();
                    throw new Error(t || `HTTP ${r.status}`);
                }
                return r.blob();
            })
            .then((blob) => {
                const link = document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = `reorganizado${zipGroupByCounterpart ? "_porcontraparte" : ""}_${filterEjercicio || "todos"}.zip`;
                link.click();
            })
            .catch((err) => {
                let msg = "Error al generar ZIP";
                try { msg = JSON.parse(err.message).detail || msg; } catch { msg = err.message || msg; }
                alert(msg);
            });
    }

    async function emailReorganized(destinatario) {
        try {
            const body = { destinatario: destinatario || null };
            if (filterEjercicio) body.ejercicio = Number(filterEjercicio);
            if (selectedEmpresa) body.empresa_id = selectedEmpresa;
            if (zipGroupByCounterpart) body.group_by_counterpart = true;
            const { data } = await api.post(`/asesor/clientes/${id}/reorganized/email`, body);
            if (data.via_link) {
                alert(`El ZIP pesa ${data.size_mb} MB (supera el máximo del email como adjunto), así que en su lugar te hemos enviado un enlace privado válido 7 días a ${data.destinatario}.\n\nEnlace: ${data.link_url}`);
            } else {
                alert(`ZIP (${data.size_mb} MB · ${data.included} facturas) enviado a ${data.destinatario}. Revisa tu bandeja.`);
            }
        } catch (e) {
            alert(formatApiError(e, "Error al enviar por email"));
        }
    }

    async function linkReorganized() {
        try {
            const body = {};
            if (filterEjercicio) body.ejercicio = Number(filterEjercicio);
            if (selectedEmpresa) body.empresa_id = selectedEmpresa;
            if (zipGroupByCounterpart) body.group_by_counterpart = true;
            const { data } = await api.post(`/asesor/clientes/${id}/reorganized/link`, body);
            const ok = window.prompt(
                `Enlace privado generado (válido 7 días · ${data.size_mb} MB · ${data.included} facturas).\n\nCópialo para guardarlo o pegarlo en Google Drive:`,
                data.url
            );
            if (ok !== null) {
                try { await navigator.clipboard.writeText(data.url); } catch {}
            }
        } catch (e) {
            alert(formatApiError(e, "Error al generar enlace"));
        }
    }

    function downloadLibro(tipo) {
        const token = localStorage.getItem("gf347_token");
        const qs = [];
        if (filterEjercicio) qs.push(`ejercicio=${filterEjercicio}`);
        if (selectedEmpresa) qs.push(`empresa_id=${selectedEmpresa}`);
        if (skipDupsInExcel) qs.push(`skip_duplicates=1`);
        const params = qs.length ? `?${qs.join("&")}` : "";
        const url = `${API}/asesor/clientes/${id}/libro/${tipo}.xlsx${params}`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const link = document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = `libro_${tipo}_${filterEjercicio || "todos"}.xlsx`;
                link.click();
            })
            .catch(() => alert("Error al descargar libro"));
    }

    if (loading && !cliente) {
        return <div className="min-h-screen flex items-center justify-center"><span className="elegant-loader"><span /><span /><span /></span></div>;
    }

    return (
        <div className="min-h-screen bg-[var(--gf-bg)]">
            <Navbar />
            <main className="max-w-7xl mx-auto px-4 py-8">
                <Link to="/portal-asesor" className="inline-flex items-center gap-1 text-xs text-[var(--gf-text-muted)] hover:text-[var(--gf-navy)] mb-4" data-testid="btn-back">
                    <ArrowLeft size={14} /> Volver a clientes
                </Link>

                {cliente && (
                    <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                        <div>
                            <h1 className="serif text-3xl text-[var(--gf-navy)]" data-testid="cliente-nombre">{cliente.nombre}</h1>
                            <div className="mono text-sm text-[var(--gf-text-muted)] mt-1">{cliente.cif}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Link to={`/portal-asesor/${id}/directorio`} className="btn-outline text-sm inline-flex items-center gap-1" data-testid="btn-directorio" title="Ver el maestro de clientes y proveedores construido a partir de las facturas confirmadas">
                                <Users size={14} weight="bold" /> Directorio
                            </Link>
                            <input ref={fileRef} type="file" accept=".zip" onChange={onSelectFile} className="hidden" data-testid="input-zip" />
                            <button onClick={() => fileRef.current?.click()} className="btn-gold" disabled={uploadJob?.status === "running" || uploadJob?.status === "queued"} data-testid="btn-upload-zip">
                                <UploadSimple size={16} weight="bold" />
                                {uploadJob?.status === "running" || uploadJob?.status === "queued" ? "Procesando…" : "Subir ZIP"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Empresas del cliente */}
                {cliente && (
                    <div className="gf-card mb-4" data-testid="empresas-panel">
                        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                            <div>
                                <h3 className="serif text-lg text-[var(--gf-navy)]">Empresas</h3>
                                <p className="text-xs text-[var(--gf-text-secondary)]">Cada cliente puede tener varias sociedades. Las facturas van asociadas a la empresa que selecciones al subir cada ZIP.</p>
                            </div>
                            <button onClick={() => setShowNewEmp(true)} className="btn-outline text-sm" data-testid="btn-new-empresa">
                                + Empresa
                            </button>
                        </div>
                        {empresas.length === 0 ? (
                            <p className="text-sm text-[var(--gf-text-muted)] italic">Aún no hay empresas. Añade la primera para poder subir facturas.</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setSelectedEmpresa("")}
                                    className={`px-3 py-1.5 text-xs border transition ${!selectedEmpresa ? "bg-[var(--gf-navy)] text-white border-[var(--gf-navy)]" : "bg-white border-[var(--gf-border)] hover:border-[var(--gf-navy)]"}`}
                                    data-testid="filter-empresa-todas"
                                >
                                    Todas ({empresas.reduce((s, e) => s + (e.facturas_count || 0), 0)})
                                </button>
                                {empresas.map((e) => (
                                    <div key={e.id} className={`inline-flex items-stretch border transition ${selectedEmpresa === e.id ? "border-[var(--gf-gold)]" : "border-[var(--gf-border)]"}`}>
                                        <button
                                            onClick={() => setSelectedEmpresa(e.id)}
                                            className={`px-3 py-1.5 text-xs ${selectedEmpresa === e.id ? "bg-[var(--gf-gold)] text-white" : "bg-white hover:bg-[var(--gf-cream)]"}`}
                                            title={e.cif}
                                            data-testid={`filter-empresa-${e.id}`}
                                        >
                                            <span className="font-semibold">{e.nombre}</span>
                                            <span className="ml-1 opacity-70">({e.facturas_count || 0})</span>
                                        </button>
                                        <button onClick={() => deleteEmpresa(e)} className="px-1.5 border-l border-inherit text-[var(--gf-danger)] hover:bg-red-50" title="Eliminar empresa" data-testid={`btn-del-emp-${e.id}`}>
                                            <Trash size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {error && (
                    <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50 mb-4">{error}</div>
                )}

                {/* Progreso de upload */}
                {uploadJob && !["done", "crashed", "interrupted", "cancelled"].includes(uploadJob.status) && (
                    <div className={`gf-card border-l-4 mb-6 sticky top-0 z-30 shadow-lg ${uploadJob.stalled ? "border-l-red-500 bg-red-50" : "border-l-[var(--gf-gold)] bg-[var(--gf-cream)]"}`} data-testid="upload-progress">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-sm inline-flex items-center gap-2">
                                {!uploadJob.stalled && <ArrowsClockwise size={18} weight="bold" className="animate-spin text-[var(--gf-gold)]" />}
                                <strong>{uploadJob.stalled ? "Proceso bloqueado" : "Procesando facturas con IA"}</strong>
                                {!uploadJob.stalled && <span className="gf-dots">…</span>}
                                <span className="ml-2 mono text-xs">({uploadJob.processed || 0}/{uploadJob.total || "?"} · fallidos {uploadJob.failed || 0})</span>
                                {uploadJob.total > 0 && (
                                    <span className="mono text-xs text-[var(--gf-navy)] font-semibold">· {Math.round(((uploadJob.processed + uploadJob.failed) / uploadJob.total) * 100)}%</span>
                                )}
                            </div>
                            <button onClick={cancelUpload} className="btn-outline text-xs" data-testid="btn-cancel-upload">
                                Cancelar
                            </button>
                        </div>
                        <div className="relative h-2 bg-white border border-[var(--gf-border)] mt-2 overflow-hidden">
                            <div className={`h-full transition-all duration-500 ${uploadJob.stalled ? "bg-red-400" : "bg-[var(--gf-gold)]"}`} style={{ width: uploadJob.total ? `${Math.round(((uploadJob.processed + uploadJob.failed) / uploadJob.total) * 100)}%` : "0%" }} />
                            {!uploadJob.stalled && <div className="absolute inset-0 gf-progress-indet opacity-40 pointer-events-none" />}
                        </div>
                        {uploadJob.stalled && (
                            <p className="text-xs text-red-700 mt-2">
                                {uploadJob.stall_reason || "El proceso lleva más de 3 minutos sin avanzar."} Cancela y vuelve a subir el ZIP (se reanudará desde donde iba).
                            </p>
                        )}
                        {uploadJob.last_file && !uploadJob.stalled && (
                            <p className="text-xs text-[var(--gf-text-secondary)] mt-2 mono truncate" title={uploadJob.last_file}>
                                Último archivo: {uploadJob.last_file}
                            </p>
                        )}
                        <p className="text-xs text-[var(--gf-text-muted)] mt-1">Cada factura tarda ≈ 5 seg. Puedes cerrar esta pestaña — el proceso continúa en background.</p>
                    </div>
                )}
                {uploadJob?.status === "interrupted" && (
                    <div className="gf-card border-l-4 border-l-red-500 bg-red-50 mb-6 flex items-center justify-between gap-3">
                        <div className="text-sm">
                            <strong>Proceso interrumpido.</strong> {uploadJob.error || "El servidor se reinició. Vuelve a subir el mismo ZIP para reanudarlo."}
                        </div>
                        <button onClick={() => setUploadJob(null)} className="btn-outline text-xs">Cerrar</button>
                    </div>
                )}
                {uploadJob?.status === "cancelled" && (
                    <div className="gf-card border-l-4 border-l-slate-400 bg-slate-50 mb-6 flex items-center justify-between gap-3">
                        <div className="text-sm"><strong>Proceso cancelado.</strong> Las facturas ya procesadas se han conservado.</div>
                        <button onClick={() => setUploadJob(null)} className="btn-outline text-xs">Cerrar</button>
                    </div>
                )}
                {uploadJob?.status === "crashed" && (
                    <div className="gf-card border-l-4 border-l-red-500 bg-red-50 mb-6">
                        <strong className="text-sm">Error procesando el ZIP:</strong>
                        <div className="text-xs mono mt-1">{uploadJob.error}</div>
                    </div>
                )}
                {uploadJob?.status === "done" && (uploadJob.failed || 0) > 0 && (
                    <div className="gf-card border-l-4 border-l-amber-500 bg-amber-50 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="upload-done-with-failed">
                        <div className="text-sm">
                            <strong>Proceso terminado.</strong> {uploadJob.processed} facturas procesadas, <span className="text-amber-800 font-semibold">{uploadJob.failed} fallidas</span>.
                        </div>
                        <div className="flex gap-2">
                            <button onClick={retryFailed} className="btn-outline text-xs" data-testid="btn-retry-failed">
                                <ArrowsClockwise size={12} /> Reintentar fallidas ({uploadJob.failed})
                            </button>
                            <button onClick={() => setUploadJob(null)} className="btn-outline text-xs">Cerrar</button>
                        </div>
                    </div>
                )}
                {uploadJob?.status === "done" && (uploadJob.failed || 0) === 0 && uploadJob.processed > 0 && (
                    <div className="sticky top-0 z-40 gf-card border-l-4 border-l-emerald-500 bg-emerald-50 mb-6 flex items-center justify-between gap-3 shadow-xl animate-pulse-once" data-testid="upload-done-ok">
                        <div className="text-sm">
                            <CheckCircle size={18} weight="fill" className="inline mr-1 text-emerald-700 align-text-bottom" />
                            <strong>¡Todo procesado correctamente!</strong> {uploadJob.processed} facturas guardadas.
                        </div>
                        <button onClick={() => setUploadJob(null)} className="btn-outline text-xs">Cerrar</button>
                    </div>
                )}

                {/* Progreso de reprocesado — sticky en la parte superior mientras corre */}
                {reprocJob && reprocJob.status === "blocked" && (
                    <div className="sticky top-0 z-40 bg-amber-100 border-b-2 border-amber-400 text-amber-900 shadow-xl -mx-6 md:-mx-10 mb-6 px-6 md:px-10 py-4" data-testid="reproc-blocked">
                        <div className="max-w-6xl mx-auto flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                                <Warning size={22} weight="fill" className="text-amber-600 mt-0.5" />
                                <div>
                                    <div className="text-sm font-bold uppercase tracking-wide">No se puede reprocesar</div>
                                    <div className="text-sm mt-1">{reprocJob.message}</div>
                                    <div className="text-xs mt-2 opacity-80">
                                        💡 Estas facturas se subieron antes de guardar el PDF en el servidor. Para reprocesar con la IA mejorada,
                                        vuelve a subir el ZIP desde <b>Portal → Subida Masiva</b> — se guarda el PDF y usará el motor nuevo (GPT-4o + auto-corrección CIF).
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => setReprocJob(null)} className="text-amber-900 hover:text-amber-700 text-lg leading-none" data-testid="btn-close-blocked">×</button>
                        </div>
                    </div>
                )}
                {reprocJob && reprocJob.status !== "done" && reprocJob.status !== "blocked" && (
                    <div className="sticky top-0 z-40 bg-[var(--gf-navy)] text-white shadow-xl -mx-6 md:-mx-10 mb-6 px-6 md:px-10 py-4" data-testid="reproc-progress">
                        <div className="max-w-6xl mx-auto">
                            <div className="flex items-center gap-3 flex-wrap">
                                <ArrowsClockwise size={22} weight="bold" className="animate-spin text-[var(--gf-gold)]" />
                                <div className="flex-1 min-w-[240px]">
                                    <div className="text-sm font-semibold uppercase tracking-wide">
                                        Reprocesando con IA · {reprocJob.processed || 0} / {reprocJob.total || "?"}
                                    </div>
                                    <div className="text-xs opacity-80 mt-0.5">
                                        Actualizadas: <b>{reprocJob.updated || 0}</b>
                                        {reprocJob.failed > 0 && <> · Fallidas: <b>{reprocJob.failed}</b></>}
                                        {reprocJob.skipped_no_file > 0 && <> · Sin PDF guardado: {reprocJob.skipped_no_file}</>}
                                        {reprocJob.status === "queued" && <> · <span className="text-[var(--gf-gold)]">preparando…</span></>}
                                    </div>
                                </div>
                                <div className="mono text-lg font-bold text-[var(--gf-gold)]">
                                    {reprocJob.total ? `${Math.round((reprocJob.processed / reprocJob.total) * 100)}%` : "0%"}
                                </div>
                            </div>
                            <div className="h-2 bg-white/20 mt-3 overflow-hidden">
                                <div className="h-full bg-[var(--gf-gold)] transition-all duration-500" style={{ width: reprocJob.total ? `${Math.round((reprocJob.processed / reprocJob.total) * 100)}%` : "0%" }} />
                            </div>
                        </div>
                    </div>
                )}
                {reprocJob?.status === "done" && (
                    <div className="gf-card border-l-4 border-l-emerald-500 bg-emerald-50 mb-6 flex items-center justify-between gap-3" data-testid="reproc-done">
                        <div className="text-sm">
                            <strong>Reprocesado completado.</strong> {reprocJob.updated} actualizadas · {reprocJob.failed} fallidas
                            {reprocJob.skipped_no_file > 0 && <> · {reprocJob.skipped_no_file} sin archivo</>}
                        </div>
                        <button onClick={() => setReprocJob(null)} className="btn-outline text-xs">Cerrar</button>
                    </div>
                )}

                {/* Resumen por ejercicio */}
                {resumen.length > 0 && (
                    <div className="gf-card mb-6" data-testid="resumen-panel">
                        <h3 className="serif text-lg text-[var(--gf-navy)] mb-3">Resumen por ejercicio</h3>
                        <div className="overflow-x-auto">
                            <table className="data-table w-full">
                                <thead>
                                    <tr>
                                        <th>Ejercicio</th>
                                        <th className="text-right"># Emitidas</th>
                                        <th className="text-right">Base Emit.</th>
                                        <th className="text-right">IVA Emit.</th>
                                        <th className="text-right"># Recibidas</th>
                                        <th className="text-right">Base Recib.</th>
                                        <th className="text-right">IVA Recib.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resumen.map((r) => (
                                        <tr key={r.ejercicio}>
                                            <td className="font-semibold">{r.ejercicio}</td>
                                            <td className="text-right mono">{r.emitidas_count}</td>
                                            <td className="text-right mono">{EUR(r.emitidas_base)}</td>
                                            <td className="text-right mono">{EUR(r.emitidas_iva)}</td>
                                            <td className="text-right mono">{r.recibidas_count}</td>
                                            <td className="text-right mono">{EUR(r.recibidas_base)}</td>
                                            <td className="text-right mono">{EUR(r.recibidas_iva)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Filtros + descarga libros */}
                <div className={cliente?.is_saco ? "grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-4 items-start" : ""} data-testid={cliente?.is_saco ? "saco-layout" : undefined}>
                    {cliente?.is_saco && (
                        <aside className="gf-card lg:sticky lg:top-4 self-start" data-testid="saco-sidebar">
                            <h3 className="serif text-base text-[var(--gf-navy)] mb-1">Ficheros de origen</h3>
                            <p className="text-[11px] text-[var(--gf-text-muted)] mb-3 leading-snug">Estas facturas fueron al saco desde estas subidas. Pulsa un fichero para ver sólo sus facturas y localizarlas más rápido.</p>
                            <div className="flex flex-col gap-1">
                                <button
                                    type="button"
                                    onClick={() => setFilterSource("")}
                                    className={`text-left px-2 py-1.5 text-xs border transition ${!filterSource ? "bg-[var(--gf-navy)] text-white border-[var(--gf-navy)]" : "bg-white border-[var(--gf-border)] hover:border-[var(--gf-navy)]"}`}
                                    data-testid="btn-source-all"
                                >
                                    <span className="font-semibold">Todos los ficheros</span>
                                    <span className="ml-1 opacity-70">({facturas.length})</span>
                                </button>
                                {sources.length === 0 ? (
                                    <p className="text-xs text-[var(--gf-text-muted)] italic mt-1">Sin ficheros.</p>
                                ) : (
                                    sources.map((s) => {
                                        const active = filterSource === s.id;
                                        const dt = s.created_at ? new Date(s.created_at) : null;
                                        const dtLabel = dt && !isNaN(dt) ? dt.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "";
                                        return (
                                            <button
                                                key={s.id}
                                                type="button"
                                                onClick={() => setFilterSource(active ? "" : s.id)}
                                                className={`text-left px-2 py-1.5 text-xs border transition ${active ? "bg-[var(--gf-gold)] text-white border-[var(--gf-gold)]" : "bg-white border-[var(--gf-border)] hover:border-[var(--gf-gold)]"}`}
                                                title={s.filename}
                                                data-testid={`btn-source-${s.id}`}
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="font-semibold truncate">{s.filename}</span>
                                                    <span className={`shrink-0 ${active ? "text-white/80" : "text-[var(--gf-text-muted)]"}`}>{s.count}</span>
                                                </div>
                                                {dtLabel && (
                                                    <div className={`text-[10px] mt-0.5 ${active ? "text-white/70" : "text-[var(--gf-text-muted)]"}`}>{dtLabel}</div>
                                                )}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </aside>
                    )}
                    <div className="min-w-0">
                <div className="gf-card mb-4">
                    <div className="flex flex-wrap items-center gap-3 justify-between">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[10px] tracking-widest uppercase text-[var(--gf-text-muted)]">Filtrar:</span>
                            <select value={filterEjercicio} onChange={(e) => setFilterEjercicio(e.target.value)} className="gf-input text-sm py-1" data-testid="filter-ejercicio">
                                <option value="">Todos los ejercicios</option>
                                {ejercicios.map((e) => <option key={e} value={e}>{e}</option>)}
                            </select>
                            <select value={filterTrimestre} onChange={(e) => setFilterTrimestre(e.target.value)} className="gf-input text-sm py-1" data-testid="filter-trimestre" title="Filtra por trimestre fiscal">
                                <option value="">Todos los trimestres</option>
                                <option value="1">T1 (Ene-Mar)</option>
                                <option value="2">T2 (Abr-Jun)</option>
                                <option value="3">T3 (Jul-Sep)</option>
                                <option value="4">T4 (Oct-Dic)</option>
                            </select>
                            <select value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)} className="gf-input text-sm py-1" data-testid="filter-tipo">
                                <option value="">Todos los tipos</option>
                                <option value="emitida">Emitidas</option>
                                <option value="recibida">Recibidas</option>
                            </select>
                            <select value={filterContraparte} onChange={(e) => setFilterContraparte(e.target.value)} className="gf-input text-sm py-1" data-testid="filter-contraparte" title="Filtra por cliente (si es emitida) o proveedor (si es recibida)" style={{ maxWidth: 240 }}>
                                <option value="">Todos los clientes / proveedores</option>
                                {(() => {
                                    const set = new Map();
                                    for (const f of facturas) {
                                        const nombre = (f.tipo === "emitida" ? f.receptor_nombre : f.emisor_nombre) || "";
                                        const cif = (f.tipo === "emitida" ? f.receptor_cif : f.emisor_cif) || "";
                                        const key = `${cif}|${nombre}`.trim();
                                        if (!key || key === "|") continue;
                                        if (!set.has(key)) set.set(key, { nombre, cif });
                                    }
                                    return [...set.entries()]
                                        .sort((a, b) => (a[1].nombre || "").localeCompare(b[1].nombre || "", "es"))
                                        .map(([key, v]) => (
                                            <option key={key} value={key}>
                                                {v.nombre || "(sin nombre)"}{v.cif ? ` · ${v.cif}` : ""}
                                            </option>
                                        ));
                                })()}
                            </select>
                            <label className="inline-flex items-center gap-1 text-xs text-[var(--gf-text-secondary)] pl-2 cursor-pointer select-none">
                                <input type="checkbox" checked={onlyWarnings} onChange={(e) => setOnlyWarnings(e.target.checked)} data-testid="filter-warnings" />
                                Sólo con avisos
                            </label>
                            <label className="inline-flex items-center gap-1 text-xs text-[var(--gf-text-secondary)] cursor-pointer select-none">
                                <input type="checkbox" checked={onlyDuplicates} onChange={(e) => setOnlyDuplicates(e.target.checked)} data-testid="filter-duplicates" />
                                Sólo duplicadas {totalDuplicates > 0 && <span className="text-rose-600 font-semibold">({totalDuplicates})</span>}
                            </label>
                            <button
                                type="button"
                                onClick={() => setFilterIntracom((v) => !v)}
                                className={`inline-flex items-center gap-1 text-xs px-2 py-1 border transition ${filterIntracom ? "bg-sky-600 text-white border-sky-600" : "bg-white border-[var(--gf-border)] hover:border-sky-500 text-[var(--gf-text-secondary)]"}`}
                                title="Sólo facturas intracomunitarias (CIF con prefijo UE ≠ ES)"
                                data-testid="filter-intracom"
                            >
                                🇪🇺 Intracom
                                {(() => { const n = facturas.filter((f) => f.is_intracom).length; return n > 0 ? <span className={filterIntracom ? "opacity-90" : "text-sky-700 font-semibold"}> ({n})</span> : null; })()}
                            </button>
                            <button
                                type="button"
                                onClick={() => setFilterInversion((v) => !v)}
                                className={`inline-flex items-center gap-1 text-xs px-2 py-1 border transition ${filterInversion ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-[var(--gf-border)] hover:border-indigo-500 text-[var(--gf-text-secondary)]"}`}
                                title="Sólo facturas marcadas como Bien de inversión"
                                data-testid="filter-inversion"
                            >
                                🏗️ Bienes inversión
                                {(() => { const n = facturas.filter((f) => f.is_inversion).length; return n > 0 ? <span className={filterInversion ? "opacity-90" : "text-indigo-700 font-semibold"}> ({n})</span> : null; })()}
                            </button>
                            <select
                                value={filterTipoGasto}
                                onChange={(e) => setFilterTipoGasto(e.target.value)}
                                className="gf-input !py-1 !px-2 !text-xs"
                                title="Filtrar por cuenta contable (PGC)"
                                data-testid="filter-tipogasto"
                            >
                                <option value="">🧾 Cuenta contable · Todas</option>
                                <option value="__none__">🆕 Sin clasificar</option>
                                {tiposGasto.map((t) => (
                                    <option key={t.codigo} value={t.codigo}>{t.codigo} · {t.nombre}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                            <label className="inline-flex items-center gap-1 text-xs text-[var(--gf-text-secondary)] cursor-pointer select-none border border-[var(--gf-border)] px-2 py-1" title="Al descargar el libro IVA, deja fuera las facturas duplicadas para no contarlas dos veces">
                                <input type="checkbox" checked={skipDupsInExcel} onChange={(e) => setSkipDupsInExcel(e.target.checked)} data-testid="toggle-skip-dups-excel" />
                                Sin duplicados en Excel
                            </label>
                            <button onClick={swapTipos} className="btn-outline text-sm" title="Invierte emitida↔recibida (e intercambia emisor/receptor) en el filtro actual. Ideal si un lote entero salió al revés." data-testid="btn-swap-tipos">
                                <ArrowsClockwise size={14} /> Invertir tipos
                            </button>
                            <button
                                onClick={() => {
                                    if (selectedIds.size === 0) {
                                        alert("Primero marca con las casillas ☑ (columna izquierda) las facturas que quieres mover.");
                                        return;
                                    }
                                    openMoveModal();
                                }}
                                className="text-sm inline-flex items-center gap-1 px-3 py-1.5 bg-[var(--gf-gold)] text-white border border-[var(--gf-gold)] hover:opacity-90 font-semibold"
                                title="Mueve las facturas seleccionadas a otra empresa del mismo cliente"
                                data-testid="btn-move-toolbar"
                            >
                                🏢 Mover a empresa{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                            </button>
                            <button onClick={() => reprocessBulk({ only_warnings: true })} className="btn-outline text-sm" disabled={reprocJob?.status === "running" || reprocJob?.status === "queued"} title="Vuelve a pasar la IA sólo sobre las facturas marcadas con avisos" data-testid="btn-reproc-warnings">
                                <ArrowsClockwise size={14} /> Reprocesar avisos
                            </button>
                            <button onClick={() => setShowZipModal(true)} className="btn-outline text-sm" title="Descarga, envía por email o genera enlace del ZIP con las facturas reorganizadas por Empresa/Ejercicio/Emitidas o Recibidas" data-testid="btn-reorganized">
                                <Archive size={14} /> ZIP reorganizado
                            </button>
                            <button onClick={() => downloadLibro("emitidas")} className="btn-outline text-sm" data-testid="btn-libro-emitidas">
                                <FileArrowDown size={14} /> Libro Emitidas
                            </button>
                            <button onClick={() => downloadLibro("recibidas")} className="btn-outline text-sm" data-testid="btn-libro-recibidas">
                                <FileArrowDown size={14} /> Libro Recibidas
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabla de facturas */}
                <div className="gf-card">
                    {(() => {
                        // Normalizador: sin acentos, minúsculas, sin puntuación
                        const norm = (v) => String(v ?? "")
                            .toLowerCase()
                            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                            .replace(/[.\-\s/]/g, "");
                        const q = norm(searchText);
                        let visibles = facturas;
                        if (filterTrimestre) visibles = visibles.filter((f) => String(f.trimestre) === filterTrimestre);
                        if (filterSource) visibles = visibles.filter((f) => f.source_id === filterSource);
                        if (filterIntracom) visibles = visibles.filter((f) => !!f.is_intracom);
                        if (filterInversion) visibles = visibles.filter((f) => !!f.is_inversion);
                        if (filterTipoGasto) {
                            if (filterTipoGasto === "__none__") visibles = visibles.filter((f) => f.tipo === "recibida" && !f.tipo_gasto_codigo);
                            else visibles = visibles.filter((f) => f.tipo_gasto_codigo === filterTipoGasto);
                        }
                        if (filterContraparte) {
                            visibles = visibles.filter((f) => {
                                const nombre = (f.tipo === "emitida" ? f.receptor_nombre : f.emisor_nombre) || "";
                                const cif = (f.tipo === "emitida" ? f.receptor_cif : f.emisor_cif) || "";
                                return `${cif}|${nombre}`.trim() === filterContraparte;
                            });
                        }
                        if (onlyWarnings) visibles = visibles.filter((f) => (f.warnings || []).length > 0);
                        if (onlyDuplicates) visibles = visibles.filter((f) => f.is_duplicate);
                        if (q) {
                            visibles = visibles.filter((f) => {
                                const hay = [
                                    f.numero, f.fecha,
                                    f.emisor_nombre, f.emisor_cif,
                                    f.receptor_nombre, f.receptor_cif,
                                    f.tipo,
                                    f.base_imponible, f.cuota_iva, f.total, f.tipo_iva,
                                    (f.warnings || []).join(" "),
                                ].map(norm).join("|");
                                return hay.includes(q);
                            });
                        }
                        // Ordenación
                        const cmp = (a, b) => {
                            const numericFields = new Set(["base_imponible", "cuota_iva", "total", "tipo_iva", "suplidos"]);
                            let va = a[sortBy]; let vb = b[sortBy];
                            if (sortBy === "contraparte") {
                                va = a.tipo === "emitida" ? a.receptor_nombre : a.emisor_nombre;
                                vb = b.tipo === "emitida" ? b.receptor_nombre : b.emisor_nombre;
                            } else if (sortBy === "cif") {
                                va = a.tipo === "emitida" ? a.receptor_cif : a.emisor_cif;
                                vb = b.tipo === "emitida" ? b.receptor_cif : b.emisor_cif;
                            }
                            if (numericFields.has(sortBy)) {
                                const na = va == null ? -Infinity : Number(va);
                                const nb = vb == null ? -Infinity : Number(vb);
                                return sortDir === "asc" ? na - nb : nb - na;
                            }
                            const sa = (va == null ? "" : String(va)).toLowerCase();
                            const sb = (vb == null ? "" : String(vb)).toLowerCase();
                            if (sa < sb) return sortDir === "asc" ? -1 : 1;
                            if (sa > sb) return sortDir === "asc" ? 1 : -1;
                            return 0;
                        };
                        visibles = [...visibles].sort(cmp);
                        const totalAvisos = facturas.filter((f) => (f.warnings || []).length > 0).length;
                        const filtroActivo = onlyWarnings || onlyDuplicates || !!q || !!filterTrimestre || !!filterContraparte || !!filterSource || filterIntracom || filterInversion || !!filterTipoGasto;
                        return (
                            <>
                                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <h3 className="serif text-lg text-[var(--gf-navy)]">Facturas · {visibles.length}{filtroActivo ? ` de ${facturas.length}` : ""}</h3>
                                        {filterSource && (
                                            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-[var(--gf-gold)] bg-amber-50 text-[var(--gf-navy)]" data-testid="chip-source-active">
                                                Fichero: <b className="max-w-[220px] truncate">{(sources.find((s) => s.id === filterSource)?.filename) || "—"}</b>
                                                <button type="button" onClick={() => setFilterSource("")} className="ml-1 text-[var(--gf-danger)] hover:opacity-70" title="Quitar filtro de fichero" data-testid="btn-clear-source">×</button>
                                            </span>
                                        )}
                                        <div className="relative">
                                            <input
                                                type="search"
                                                value={searchText}
                                                onChange={(e) => setSearchText(e.target.value)}
                                                placeholder="Buscar en cualquier campo… (nº, fecha, razón, CIF, importe, aviso)"
                                                className="gf-input text-sm py-1 pr-8"
                                                style={{ minWidth: 320 }}
                                                data-testid="filter-search-text"
                                            />
                                            {searchText && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSearchText("")}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--gf-text-muted)] hover:text-[var(--gf-danger)] text-lg leading-none"
                                                    title="Limpiar búsqueda"
                                                    data-testid="btn-clear-search"
                                                >×</button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {selectedIds.size > 0 && (
                                            <>
                                                <span className="text-xs bg-[var(--gf-navy)]/10 text-[var(--gf-navy)] px-2 py-1 border border-[var(--gf-navy)]/20 font-semibold" data-testid="selected-badge">
                                                    {selectedIds.size} seleccionadas
                                                </span>
                                                {empresas.length >= 2 ? (
                                                    <button
                                                        onClick={openMoveModal}
                                                        className="text-xs inline-flex items-center gap-1 px-3 py-1.5 bg-[var(--gf-gold)] text-white border border-[var(--gf-gold)] hover:opacity-90 font-semibold uppercase tracking-wide"
                                                        title="Mover las facturas seleccionadas a otra empresa del mismo cliente. Se recalcularán los avisos con el CIF de la empresa destino."
                                                        data-testid="btn-move-selected"
                                                    >
                                                        🏢 Mover a otra EMPRESA
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => {
                                                            if (confirm("Solo tienes 1 empresa en este cliente. Para mover facturas necesitas crear otra empresa antes.\n\n¿Abrir el diálogo de nueva empresa ahora?")) {
                                                                setShowNewEmp(true);
                                                            }
                                                        }}
                                                        className="text-xs inline-flex items-center gap-1 px-3 py-1.5 bg-[var(--gf-gold)] text-white border border-[var(--gf-gold)] hover:opacity-90 font-semibold uppercase tracking-wide"
                                                        title="Todavía no hay otra empresa donde mover. Pulsa para crear una segunda empresa."
                                                        data-testid="btn-move-selected"
                                                    >
                                                        🏢 Mover a otra EMPRESA
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => confirmSelected(true)}
                                                    className="text-xs inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 font-semibold uppercase tracking-wide"
                                                    title="Marcar como CONFIRMADAS (revisadas por el asesor). Solo las confirmadas entran en el Directorio y en los libros IVA."
                                                    data-testid="btn-confirm-selected"
                                                >
                                                    <CheckCircle size={12} weight="fill" /> Confirmar ({selectedIds.size})
                                                </button>
                                                <button
                                                    onClick={() => confirmSelected(false)}
                                                    className="text-xs text-[var(--gf-text-muted)] hover:text-[var(--gf-danger)] underline"
                                                    title="Quitar la marca de confirmada"
                                                    data-testid="btn-unconfirm-selected"
                                                >
                                                    Quitar confirmación
                                                </button>
                                                <button onClick={swapTiposSelected} className="btn-outline text-xs" title="Invertir emitida↔recibida en las facturas marcadas (intercambia emisor y receptor)" data-testid="btn-swap-selected">
                                                    ⇄ Cambiar TIPO (emitida↔recibida)
                                                </button>
                                                <button
                                                    onClick={() => reprocessBulk({ ids: Array.from(selectedIds) })}
                                                    className="text-xs inline-flex items-center gap-1 px-3 py-1.5 bg-[var(--gf-navy)] text-white border border-[var(--gf-navy)] hover:opacity-90 font-semibold uppercase tracking-wide"
                                                    disabled={reprocJob?.status === "running" || reprocJob?.status === "queued"}
                                                    title="Reprocesar con IA las facturas seleccionadas (motor mejorado)"
                                                    data-testid="btn-reproc-selected"
                                                >
                                                    <ArrowsClockwise size={12} weight="bold" /> Reprocesar IA ({selectedIds.size})
                                                </button>
                                                <button onClick={() => setSelectedIds(new Set())} className="text-xs text-[var(--gf-text-muted)] hover:text-[var(--gf-danger)] underline" data-testid="btn-clear-selection">
                                                    Limpiar
                                                </button>
                                            </>
                                        )}
                                        {totalAvisos > 0 && (
                                            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 border border-amber-300 inline-flex items-center gap-1" data-testid="warnings-badge">
                                                <Warning size={12} weight="fill" /> {totalAvisos} con avisos
                                            </span>
                                        )}
                                        {totalDuplicates > 0 && (
                                            <>
                                                <span className="text-xs bg-rose-100 text-rose-800 px-2 py-1 border border-rose-300 inline-flex items-center gap-1" data-testid="duplicates-badge">
                                                    <CopySimple size={12} weight="fill" /> {totalDuplicates} duplicadas
                                                </span>
                                                <button onClick={deleteDuplicates} className="btn-outline text-xs text-rose-700 border-rose-300 hover:bg-rose-50" title="Borra todas las facturas duplicadas dejando sólo la más antigua de cada grupo" data-testid="btn-delete-duplicates">
                                                    <Trash size={12} /> Borrar duplicadas
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {visibles.length === 0 ? (
                                    <p className="text-sm text-[var(--gf-text-secondary)] text-center py-8">
                                        {q ? `No hay facturas que coincidan con "${searchText}".` : onlyDuplicates ? "No hay facturas duplicadas en este filtro." : onlyWarnings ? "No hay facturas con avisos en este filtro." : "Aún no hay facturas. Sube un ZIP para empezar."}
                                    </p>
                                ) : (
                        <div className="overflow-x-auto">
                            <table className="data-table data-table--compact w-full" data-testid="facturas-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 30 }}>
                                            <input
                                                type="checkbox"
                                                checked={visibles.length > 0 && visibles.every((f) => selectedIds.has(f.id))}
                                                onChange={() => toggleSelectAll(visibles.map((f) => f.id))}
                                                data-testid="check-all"
                                                title="Seleccionar/deseleccionar todas las facturas visibles"
                                            />
                                        </th>
                                        <th style={{ width: 24 }}></th>
                                        <th onClick={() => toggleSort("fecha")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)]" title="Ordenar por fecha" data-testid="sort-fecha">Fecha{sortArrow("fecha")}</th>
                                        <th onClick={() => toggleSort("tipo")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)]" title="Ordenar por tipo" data-testid="sort-tipo">Tipo{sortArrow("tipo")}</th>
                                        <th onClick={() => toggleSort("numero")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)]" title="Ordenar por número" data-testid="sort-numero">Nº{sortArrow("numero")}</th>
                                        <th onClick={() => toggleSort("contraparte")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)]" title="Ordenar por razón social" data-testid="sort-contraparte">Razón Social{sortArrow("contraparte")}</th>
                                        <th onClick={() => toggleSort("cif")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)]" title="Ordenar por CIF" data-testid="sort-cif">CIF{sortArrow("cif")}</th>
                                        <th onClick={() => toggleSort("base_imponible")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)] text-right" title="Ordenar por base" data-testid="sort-base">Base{sortArrow("base_imponible")}</th>
                                        <th onClick={() => toggleSort("cuota_iva")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)] text-right" title="Ordenar por IVA" data-testid="sort-iva">IVA{sortArrow("cuota_iva")}</th>
                                        <th onClick={() => toggleSort("suplidos")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)] text-right" title="Suplidos (IVA 0)" data-testid="sort-suplidos">Suplidos{sortArrow("suplidos")}</th>
                                        <th className="text-left" title="Cuenta contable PGC (solo recibidas)" data-testid="col-cuenta">Cuenta</th>
                                        <th onClick={() => toggleSort("total")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)] text-right" title="Ordenar por total" data-testid="sort-total">Total{sortArrow("total")}</th>
                                        <th className="text-right col-sticky-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibles.map((f) => {
                                        const contraparte = f.tipo === "emitida" ? f.receptor_nombre : f.emisor_nombre;
                                        const contracif = f.tipo === "emitida" ? f.receptor_cif : f.emisor_cif;
                                        const warns = f.warnings || [];
                                        const hasWarn = warns.length > 0;
                                        const isIrpf = warns.some((w) => w.startsWith("[IRPF]"));
                                        const isDup = f.is_duplicate;
                                        const isConfirmed = !!f.confirmed;
                                        const rowCls = isConfirmed ? "bg-emerald-50/40" : (isDup ? "bg-rose-50" : (hasWarn ? (isIrpf ? "bg-yellow-50" : "bg-amber-50/60") : ""));
                                        const iconCls = isIrpf ? "text-yellow-600" : "text-amber-500";
                                        return (
                                            <tr key={f.id} data-testid={`row-${f.id}`} className={rowCls}>
                                                <td className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.has(f.id)}
                                                        onChange={() => toggleSelect(f.id)}
                                                        data-testid={`check-${f.id}`}
                                                    />
                                                </td>
                                                <td className="text-center">
                                                    <div className="inline-flex items-center gap-1">
                                                        {isConfirmed && (
                                                            <span title="Confirmada por el asesor" className="inline-block" data-testid={`confirmed-${f.id}`}>
                                                                <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                                                            </span>
                                                        )}
                                                        {isDup && (
                                                            <span title={`Duplicada: hay ${f.duplicate_count} facturas con el mismo nº+fecha+emisor`} className="inline-block cursor-help" data-testid={`dup-${f.id}`}>
                                                                <CopySimple size={16} weight="fill" className="text-rose-600" />
                                                            </span>
                                                        )}
                                                        {hasWarn && (
                                                            <span title={warns.map((w) => w.replace(/^\[IRPF\]\s*/, "IRPF: ")).join("\n")} className="inline-block cursor-help" data-testid={`warn-${f.id}`}>
                                                                <Warning size={16} weight="fill" className={iconCls} />
                                                            </span>
                                                        )}
                                                        {f.is_intracom && (
                                                            <span title="Operación intracomunitaria (CIF con prefijo UE ≠ ES)" className="inline-flex items-center justify-center h-4 min-w-[24px] px-1 text-[9px] font-bold bg-sky-100 text-sky-700 border border-sky-300 rounded-sm" data-testid={`badge-intracom-${f.id}`}>
                                                                UE
                                                            </span>
                                                        )}
                                                        {f.is_inversion && (
                                                            <span title="Bien de inversión" className="inline-flex items-center justify-center h-4 min-w-[24px] px-1 text-[9px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-300 rounded-sm" data-testid={`badge-inversion-${f.id}`}>
                                                                INV
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="mono text-xs">{f.fecha || "—"}</td>
                                                <td>
                                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${f.tipo === "emitida" ? "bg-[var(--gf-navy)]/10 text-[var(--gf-navy)]" : "bg-[var(--gf-gold)]/15 text-[var(--gf-gold)]"}`}>{f.tipo || "?"}</span>
                                                </td>
                                                <td className="mono text-xs">{f.numero || "—"}</td>
                                                <td className="text-sm max-w-[220px] truncate" title={contraparte}>{contraparte || "—"}</td>
                                                <td className="mono text-xs">{contracif || "—"}</td>
                                                <td className="text-right mono text-xs">{EUR(f.base_imponible)}</td>
                                                <td className="text-right mono text-xs">{EUR(f.cuota_iva)}</td>
                                                <td className={`text-right mono text-xs ${(f.suplidos || 0) > 0 ? "text-amber-700 font-semibold" : "text-[var(--gf-text-muted)]"}`} data-testid={`cell-suplidos-${f.id}`}>{EUR(f.suplidos || 0)}</td>
                                                <td className="text-xs" data-testid={`cell-cuenta-${f.id}`}>
                                                    {f.tipo === "recibida" ? (
                                                        f.tipo_gasto_codigo ? (
                                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-50 border border-indigo-200 text-indigo-700 mono" title={f.tipo_gasto_nombre || ""}>
                                                                {f.tipo_gasto_codigo}
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 bg-amber-50 border border-amber-300 text-amber-700 text-[10px] uppercase tracking-widest" title="Primera vez con este proveedor · asigna una cuenta contable en el modal de edición">
                                                                🆕 Sin clasificar
                                                            </span>
                                                        )
                                                    ) : <span className="text-[var(--gf-text-muted)]">—</span>}
                                                </td>
                                                <td className="text-right mono text-xs font-semibold">{EUR(f.total)}</td>
                                                <td className="text-right col-sticky-right whitespace-nowrap" style={{ minWidth: 120 }}>
                                                    <div className="inline-flex items-center gap-1">
                                                        <button onClick={() => openPreview(f)} className="p-1.5 text-[var(--gf-text-secondary)] hover:text-[var(--gf-navy)]" title={`Ver original: ${f.archivo_original || ""}`} data-testid={`btn-view-${f.id}`}>
                                                            <Eye size={14} />
                                                        </button>
                                                        <button onClick={() => reprocessOne(f)} className="p-1.5 text-[var(--gf-text-secondary)] hover:text-[var(--gf-navy)]" title="Reprocesar con IA" data-testid={`btn-reproc-${f.id}`}>
                                                            <ArrowsClockwise size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedIds(new Set([f.id]));
                                                                setTimeout(() => openMoveModal(), 0);
                                                            }}
                                                            className="p-1.5 text-[var(--gf-gold)] hover:opacity-70"
                                                            title="Mover esta factura a otra empresa"
                                                            data-testid={`btn-move-row-${f.id}`}
                                                        >
                                                            🏢
                                                        </button>
                                                        <button onClick={() => setEditing({ ...f })} className="p-1.5 text-[var(--gf-text-secondary)] hover:text-[var(--gf-navy)]" title="Editar" data-testid={`btn-edit-${f.id}`}>
                                                            <PencilSimple size={14} />
                                                        </button>
                                                        <button onClick={() => deleteFactura(f)} className="p-1.5 text-[var(--gf-danger)] hover:opacity-70" title="Eliminar" data-testid={`btn-delete-factura-${f.id}`}>
                                                            <Trash size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                                )}
                            </>
                        );
                    })()}
                </div>
                    </div>
                </div>

                {/* Modal ZIP reorganizado: 3 opciones */}
                {showZipModal && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !zipBusy && setShowZipModal(false)}>
                        <div onClick={(e) => e.stopPropagation()} className="bg-white p-6 md:p-8 max-w-lg w-full space-y-4" data-testid="modal-zip-reorganized">
                            <h2 className="serif text-2xl text-[var(--gf-navy)]">ZIP reorganizado</h2>
                            <p className="text-xs text-[var(--gf-text-secondary)]">
                                El ZIP contiene las facturas ordenadas por <b>Empresa / Ejercicio / Emitidas · Recibidas</b> con nombre <span className="font-mono">fecha_nº_contraparte.pdf</span>.
                                Respeta el ejercicio y empresa seleccionados en los filtros de arriba.
                                {filterEjercicio && <span> · Ejercicio: <b>{filterEjercicio}</b></span>}
                            </p>

                            <div className="border border-[var(--gf-border)] bg-[var(--gf-cream)]/60 p-3">
                                <div className="text-[11px] uppercase tracking-widest text-[var(--gf-text-muted)] mb-2">Estructura</div>
                                <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="zip-group-flat-label">
                                    <input
                                        type="radio"
                                        name="zip-group-mode"
                                        checked={!zipGroupByCounterpart}
                                        onChange={() => setZipGroupByCounterpart(false)}
                                        className="mt-1 accent-[var(--gf-navy)]"
                                        data-testid="zip-group-flat"
                                    />
                                    <div>
                                        <div className="text-sm font-semibold text-[var(--gf-navy)]">📁 Estándar (por tipo)</div>
                                        <div className="text-[11px] text-[var(--gf-text-muted)] mt-0.5 font-mono">Empresa / Ejercicio / Emitidas · Recibidas / factura.pdf</div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-2 cursor-pointer select-none mt-2" data-testid="zip-group-counterpart-label">
                                    <input
                                        type="radio"
                                        name="zip-group-mode"
                                        checked={zipGroupByCounterpart}
                                        onChange={() => setZipGroupByCounterpart(true)}
                                        className="mt-1 accent-[var(--gf-gold)]"
                                        data-testid="zip-group-counterpart"
                                    />
                                    <div>
                                        <div className="text-sm font-semibold text-[var(--gf-navy)]">👥 Agrupado por proveedor/cliente</div>
                                        <div className="text-[11px] text-[var(--gf-text-muted)] mt-0.5 font-mono">Empresa / Ejercicio / Emitidas · Recibidas / <b>Proveedor_CIF</b> / factura.pdf</div>
                                    </div>
                                </label>
                            </div>

                            <div className="space-y-3">
                                <button
                                    onClick={async () => { setZipBusy(true); try { downloadReorganized(); setShowZipModal(false); } finally { setZipBusy(false); } }}
                                    disabled={zipBusy}
                                    className="w-full text-left border border-[var(--gf-border)] p-3 hover:border-[var(--gf-gold)] hover:bg-[var(--gf-cream)] transition"
                                    data-testid="opt-download-direct"
                                >
                                    <div className="font-semibold text-[var(--gf-navy)] inline-flex items-center gap-2">📥 Descarga directa</div>
                                    <div className="text-xs text-[var(--gf-text-muted)] mt-0.5">Se guarda en la carpeta de Descargas de tu navegador.</div>
                                </button>

                                <div className="border border-[var(--gf-border)] p-3 hover:border-[var(--gf-gold)] transition">
                                    <div className="font-semibold text-[var(--gf-navy)] inline-flex items-center gap-2">📧 Enviar por email</div>
                                    <div className="text-xs text-[var(--gf-text-muted)] mb-2">
                                        Por defecto a tu email de la sesión ({user?.email}). Puedes cambiarlo abajo. Si el ZIP supera 35 MB te enviaremos un enlace privado válido 7 días.
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="email"
                                            value={zipEmailDest}
                                            onChange={(e) => setZipEmailDest(e.target.value)}
                                            placeholder={user?.email || "email@empresa.com"}
                                            className="gf-input text-sm py-1 flex-1"
                                            data-testid="zip-email-dest"
                                        />
                                        <button
                                            onClick={async () => { setZipBusy(true); try { await emailReorganized(zipEmailDest.trim() || null); setShowZipModal(false); } finally { setZipBusy(false); } }}
                                            disabled={zipBusy}
                                            className="btn-gold text-sm"
                                            data-testid="btn-zip-email"
                                        >{zipBusy ? "Enviando…" : "Enviar"}</button>
                                    </div>
                                </div>

                                <button
                                    onClick={async () => { setZipBusy(true); try { await linkReorganized(); setShowZipModal(false); } finally { setZipBusy(false); } }}
                                    disabled={zipBusy}
                                    className="w-full text-left border border-[var(--gf-border)] p-3 hover:border-[var(--gf-gold)] hover:bg-[var(--gf-cream)] transition"
                                    data-testid="opt-copy-link"
                                >
                                    <div className="font-semibold text-[var(--gf-navy)] inline-flex items-center gap-2">🔗 Copiar enlace privado (7 días)</div>
                                    <div className="text-xs text-[var(--gf-text-muted)] mt-0.5">Se genera un enlace de descarga con token único. Ideal para pegarlo en Google Drive, Dropbox o compartirlo con un compañero.</div>
                                </button>
                            </div>

                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setShowZipModal(false)} disabled={zipBusy} className="btn-outline flex-1" data-testid="btn-cancel-zip">Cerrar</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal mover facturas a otra empresa/cliente */}
                {showMoveModal && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !moving && setShowMoveModal(false)}>
                        <div onClick={(e) => e.stopPropagation()} className="bg-white p-6 md:p-8 max-w-lg w-full space-y-4" data-testid="modal-move-facturas">
                            <h2 className="serif text-2xl text-[var(--gf-navy)]">Mover facturas</h2>
                            <p className="text-xs text-[var(--gf-text-secondary)]">
                                Vas a reasignar <b>{selectedIds.size}</b> {selectedIds.size === 1 ? "factura" : "facturas"}. Puedes moverlas a otra empresa del mismo cliente o a un cliente distinto de tu asesoría. Se recalcularán los avisos con el CIF de destino (si el CIF coincide, el aviso "CIF no coincide" desaparecerá).
                            </p>
                            <div>
                                <label className="gf-label">Destino</label>
                                <select
                                    value={moveTargetCliente}
                                    onChange={(e) => setMoveTargetCliente(e.target.value)}
                                    className="gf-input"
                                    data-testid="select-move-target"
                                    autoFocus
                                >
                                    <option value="" disabled>-- Elige empresa o cliente destino --</option>
                                    {empresas.length > 0 && (
                                        <optgroup label={`▸ Empresas de este cliente (${cliente?.nombre || ""})`}>
                                            {empresas.map((em) => (
                                                <option key={em.id} value={`empresa:${em.id}`}>
                                                    {em.nombre}{em.cif ? ` · ${em.cif}` : ""}{selectedEmpresa === em.id ? "  (actual)" : ""}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    {otrosClientes.length > 0 && (
                                        <optgroup label="▸ Otros clientes de tu asesoría">
                                            {otrosClientes.map((c) => (
                                                <option key={c.id} value={`cliente:${c.id}`}>
                                                    {c.nombre}{c.cif ? ` · ${c.cif}` : ""}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                </select>
                                <p className="text-xs text-[var(--gf-text-muted)] mt-1">
                                    Al mover a otro cliente, si ese cliente aún no tiene ninguna empresa, se creará una automáticamente con su mismo CIF y nombre.
                                </p>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setShowMoveModal(false)} disabled={moving} className="btn-outline flex-1" data-testid="btn-cancel-move">Cancelar</button>
                                <button type="button" onClick={moverFacturasSelected} disabled={moving || !moveTargetCliente} className="btn-gold flex-1" data-testid="btn-confirm-move">
                                    {moving ? "Moviendo…" : `Mover ${selectedIds.size}`}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal nueva empresa */}
                {showNewEmp && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowNewEmp(false)}>
                        <form onClick={(e) => e.stopPropagation()} onSubmit={createEmpresa} className="bg-white p-6 md:p-8 max-w-md w-full space-y-4" data-testid="modal-new-empresa">
                            <h2 className="serif text-2xl text-[var(--gf-navy)]">Nueva empresa</h2>
                            <p className="text-xs text-[var(--gf-text-secondary)]">Se creará dentro de este cliente. Podrás subir un ZIP de facturas asociadas a ella.</p>
                            <div>
                                <label className="gf-label">Nombre / Razón social *</label>
                                <input value={empNombre} onChange={(e) => setEmpNombre(e.target.value)} className="gf-input" required data-testid="input-emp-nombre" autoFocus />
                            </div>
                            <div>
                                <label className="gf-label">CIF / NIF</label>
                                <input value={empCif} onChange={(e) => setEmpCif(e.target.value.toUpperCase())} className="gf-input mono" placeholder="B12345678" data-testid="input-emp-cif" />
                                <p className="text-xs text-[var(--gf-text-muted)] mt-1">Se usará para detectar automáticamente qué facturas son emitidas y cuáles recibidas.</p>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setShowNewEmp(false)} className="btn-outline flex-1">Cancelar</button>
                                <button type="submit" className="btn-gold flex-1" data-testid="btn-save-empresa">Crear empresa</button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Modal selector empresa al subir ZIP */}
                {showUploadPicker && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => { setShowUploadPicker(false); setPendingFile(null); }}>
                        <div onClick={(e) => e.stopPropagation()} className="bg-white p-6 md:p-8 max-w-md w-full space-y-4" data-testid="modal-upload-picker">
                            <h2 className="serif text-xl text-[var(--gf-navy)]">¿A qué empresa pertenece este ZIP?</h2>
                            <p className="text-xs text-[var(--gf-text-secondary)]">Archivo: <span className="mono">{pendingFile?.name}</span></p>
                            <div>
                                <label className="gf-label">Empresa</label>
                                <select value={uploadEmpresa} onChange={(e) => setUploadEmpresa(e.target.value)} className="gf-input" data-testid="select-upload-empresa">
                                    {empresas.map((em) => (
                                        <option key={em.id} value={em.id}>{em.nombre} {em.cif ? `(${em.cif})` : ""}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="gf-label">¿Qué tipo de facturas contiene este ZIP?</label>
                                <div className="grid grid-cols-3 gap-2" data-testid="upload-tipo-hint">
                                    {[
                                        { v: "recibida", t: "Recibidas", d: "Compras / gastos" },
                                        { v: "emitida", t: "Emitidas", d: "Ventas propias" },
                                        { v: "auto", t: "Mixto", d: "Que decida la IA" },
                                    ].map((o) => (
                                        <label
                                            key={o.v}
                                            className={`cursor-pointer border p-2 text-center text-xs transition-all ${uploadTipoHint === o.v ? "border-[var(--gf-gold)] bg-[var(--gf-gold)]/10" : "border-[var(--gf-border)] hover:border-[var(--gf-text-secondary)]"}`}
                                        >
                                            <input type="radio" name="tipoHint" value={o.v} checked={uploadTipoHint === o.v} onChange={(e) => setUploadTipoHint(e.target.value)} className="sr-only" />
                                            <div className="font-semibold text-[var(--gf-navy)]">{o.t}</div>
                                            <div className="text-[10px] text-[var(--gf-text-muted)] mt-0.5">{o.d}</div>
                                        </label>
                                    ))}
                                </div>
                                <p className="text-[11px] text-[var(--gf-text-muted)] mt-1">Si sabes que TODAS son del mismo tipo, márcalo; así evitamos que la IA confunda emisor/receptor.</p>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button onClick={() => { setShowUploadPicker(false); setPendingFile(null); }} className="btn-outline flex-1">Cancelar</button>
                                <button onClick={confirmUpload} className="btn-gold flex-1" data-testid="btn-confirm-upload">
                                    <UploadSimple size={14} weight="bold" /> Empezar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal edición */}
                {editing && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setEditing(null)}>
                        <form onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); saveFactura(editing); }} className="bg-white p-6 md:p-8 max-w-2xl w-full space-y-3 my-8" data-testid="modal-edit-factura">
                            <h2 className="serif text-xl text-[var(--gf-navy)]">Editar factura</h2>
                            {(() => {
                                const raw = editing.warnings || [];
                                // Filtrar avisos del saco (placeholder "Sin asignar") que no aportan nada
                                const filtered = raw.filter((w) => !/no aparece en emisor ni receptor/i.test(w) || !/'Sin asignar'|'_Saco Sin Asignar'/i.test(w));
                                if (filtered.length === 0) return null;
                                return (
                                    <div className="border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1">
                                        <strong className="flex items-center gap-1"><Warning size={12} weight="fill" /> Avisos detectados:</strong>
                                        <ul className="list-disc list-inside">
                                            {filtered.map((w, i) => <li key={i}>{w.replace(/^\[IRPF\]\s*/, "IRPF: ")}</li>)}
                                        </ul>
                                    </div>
                                );
                            })()}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="gf-label">Fecha</label>
                                    <input type="date" value={editing.fecha || ""} onChange={(e) => setEditing({ ...editing, fecha: e.target.value })} className="gf-input" />
                                </div>
                                <div>
                                    <label className="gf-label">Nº factura</label>
                                    <input value={editing.numero || ""} onChange={(e) => setEditing({ ...editing, numero: e.target.value })} className="gf-input" />
                                </div>
                                <div>
                                    <label className="gf-label">Tipo</label>
                                    <select value={editing.tipo || "recibida"} onChange={(e) => setEditing({ ...editing, tipo: e.target.value })} className="gf-input">
                                        <option value="emitida">Emitida</option>
                                        <option value="recibida">Recibida</option>
                                    </select>
                                </div>
                                <div className="flex items-end">
                                    <button
                                        type="button"
                                        onClick={() => setEditing({
                                            ...editing,
                                            emisor_nombre: editing.receptor_nombre,
                                            emisor_cif: editing.receptor_cif,
                                            receptor_nombre: editing.emisor_nombre,
                                            receptor_cif: editing.emisor_cif,
                                        })}
                                        className="btn-outline text-xs w-full"
                                        title="Intercambiar emisor y receptor (útil si la IA los confundió)"
                                        data-testid="btn-swap-partes"
                                    >
                                        ⇄ Intercambiar emisor/receptor
                                    </button>
                                </div>
                                <div>
                                    <label className="gf-label">Emisor</label>
                                    <input value={editing.emisor_nombre || ""} onChange={(e) => setEditing({ ...editing, emisor_nombre: e.target.value })} className="gf-input" />
                                </div>
                                <div>
                                    <label className="gf-label">CIF emisor</label>
                                    <input value={editing.emisor_cif || ""} onChange={(e) => setEditing({ ...editing, emisor_cif: e.target.value.toUpperCase() })} className="gf-input mono" />
                                </div>
                                <div>
                                    <label className="gf-label">Receptor</label>
                                    <input value={editing.receptor_nombre || ""} onChange={(e) => setEditing({ ...editing, receptor_nombre: e.target.value })} className="gf-input" />
                                </div>
                                <div>
                                    <label className="gf-label">CIF receptor</label>
                                    <input value={editing.receptor_cif || ""} onChange={(e) => setEditing({ ...editing, receptor_cif: e.target.value.toUpperCase() })} className="gf-input mono" />
                                </div>
                                <div>
                                    <label className="gf-label">Base imponible (€)</label>
                                    <input type="number" step="0.01" value={editing.base_imponible ?? ""} onChange={(e) => setEditing({ ...editing, base_imponible: parseFloat(e.target.value) || 0 })} className="gf-input mono" />
                                </div>
                                <div>
                                    <label className="gf-label">% IVA</label>
                                    <input type="number" step="0.01" value={editing.tipo_iva ?? ""} onChange={(e) => setEditing({ ...editing, tipo_iva: parseFloat(e.target.value) || 0 })} className="gf-input mono" />
                                </div>
                                <div>
                                    <label className="gf-label">Cuota IVA (€)</label>
                                    <input type="number" step="0.01" value={editing.cuota_iva ?? ""} onChange={(e) => setEditing({ ...editing, cuota_iva: parseFloat(e.target.value) || 0 })} className="gf-input mono" />
                                </div>
                                <div>
                                    <label className="gf-label">Retención IRPF (€)</label>
                                    <input type="number" step="0.01" value={editing.retencion ?? ""} onChange={(e) => setEditing({ ...editing, retencion: parseFloat(e.target.value) || 0 })} className="gf-input mono" />
                                </div>
                                <div>
                                    <label className="gf-label" title="Suplidos: importes pagados por cuenta del cliente. IVA a cuota cero.">Suplidos (€) · IVA 0</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editing.suplidos ?? ""}
                                        onChange={(e) => setEditing({ ...editing, suplidos: parseFloat(e.target.value) || 0 })}
                                        className="gf-input mono"
                                        placeholder="0.00"
                                        data-testid="editing-suplidos"
                                    />
                                </div>
                                <div>
                                    <label className="gf-label">Total (€)</label>
                                    <input type="number" step="0.01" value={editing.total ?? ""} onChange={(e) => setEditing({ ...editing, total: parseFloat(e.target.value) || 0 })} className="gf-input mono" />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 mt-3 cursor-pointer select-none border border-[var(--gf-border)] p-3 bg-[var(--gf-cream)]" data-testid="editing-confirmed-label">
                                <input
                                    type="checkbox"
                                    checked={!!editing.confirmed}
                                    onChange={(e) => setEditing({ ...editing, confirmed: e.target.checked })}
                                    className="w-5 h-5 accent-[var(--gf-gold)]"
                                    data-testid="editing-confirmed"
                                />
                                <div>
                                    <div className="text-sm font-semibold text-[var(--gf-navy)] inline-flex items-center gap-1">
                                        <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                                        Marcar como <span className="text-emerald-700">CONFIRMADA</span> (revisada por el asesor)
                                    </div>
                                    <div className="text-[11px] text-[var(--gf-text-muted)] mt-0.5">
                                        Solo las confirmadas se incluyen en el Directorio y en los Libros IVA definitivos.
                                    </div>
                                </div>
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                <label className="flex items-center gap-2 cursor-pointer select-none border border-[var(--gf-border)] p-2.5 bg-sky-50/60" data-testid="editing-intracom-label">
                                    <input
                                        type="checkbox"
                                        checked={!!editing.is_intracom}
                                        onChange={(e) => setEditing({ ...editing, is_intracom: e.target.checked })}
                                        className="w-5 h-5 accent-sky-600"
                                        data-testid="editing-intracom"
                                    />
                                    <div>
                                        <div className="text-sm font-semibold text-sky-800">🇪🇺 Operación intracomunitaria</div>
                                        <div className="text-[11px] text-[var(--gf-text-muted)] leading-snug">Se detecta automáticamente si el CIF tiene prefijo UE ≠ ES. Marca aquí si la IA no lo pilló bien. Va a la hoja "Intracom" del Libro.</div>
                                    </div>
                                </label>
                                {editing.tipo === "recibida" && (
                                    <label className="flex items-center gap-2 cursor-pointer select-none border border-[var(--gf-border)] p-2.5 bg-indigo-50/60" data-testid="editing-inversion-label">
                                        <input
                                            type="checkbox"
                                            checked={!!editing.is_inversion}
                                            onChange={(e) => setEditing({ ...editing, is_inversion: e.target.checked })}
                                            className="w-5 h-5 accent-indigo-600"
                                            data-testid="editing-inversion"
                                        />
                                        <div>
                                            <div className="text-sm font-semibold text-indigo-800">🏗️ Bien de inversión</div>
                                            <div className="text-[11px] text-[var(--gf-text-muted)] leading-snug">Marca la factura como adquisición de bien de inversión (inmovilizado). Va a la hoja "Bienes Inversión" del Libro de Recibidas.</div>
                                        </div>
                                    </label>
                                )}
                            </div>
                            {editing.tipo === "recibida" && (
                                <div className="mt-2 border border-[var(--gf-border)] p-3 bg-white" data-testid="editing-tipogasto-block">
                                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                                        <label className="gf-label !mb-0">🧾 Cuenta contable (PGC)</label>
                                        {!editing.tipo_gasto_codigo && editing.emisor_cif && (
                                            <span className="text-[11px] px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-800 uppercase tracking-widest" data-testid="editing-tipogasto-primera-vez">
                                                🆕 Primera vez con este proveedor
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <select
                                            value={editing.tipo_gasto_codigo || ""}
                                            onChange={(e) => {
                                                const codigo = e.target.value;
                                                if (codigo === "__new__") {
                                                    const c = prompt("Código PGC nuevo (p.ej. 640):");
                                                    if (!c) return;
                                                    const n = prompt("Nombre de la cuenta:");
                                                    if (!n) return;
                                                    createTipoGasto(c, n).then((d) => {
                                                        if (d) setEditing({ ...editing, tipo_gasto_codigo: d.codigo, tipo_gasto_nombre: d.nombre });
                                                    });
                                                    return;
                                                }
                                                const t = tiposGasto.find((x) => x.codigo === codigo);
                                                setEditing({ ...editing, tipo_gasto_codigo: codigo || null, tipo_gasto_nombre: t ? t.nombre : null });
                                            }}
                                            className="gf-input flex-1"
                                            data-testid="editing-tipogasto-select"
                                        >
                                            <option value="">— Sin clasificar —</option>
                                            {tiposGasto.map((t) => (
                                                <option key={t.codigo} value={t.codigo}>{t.codigo} · {t.nombre}</option>
                                            ))}
                                            <option value="__new__">＋ Añadir cuenta manual…</option>
                                        </select>
                                    </div>
                                    <p className="text-[11px] text-[var(--gf-text-muted)] mt-1 leading-snug">La cuenta se recuerda por CIF de proveedor: la próxima factura del mismo proveedor la asignará sola.</p>
                                </div>
                            )}
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setEditing(null)} className="btn-outline flex-1">Cancelar</button>
                                <button type="button" onClick={() => reprocessOne(editing)} className="btn-outline flex-1" title="Vuelve a extraer los datos con la IA sobre el archivo original" data-testid="btn-reproc-editing">
                                    <ArrowsClockwise size={14} /> Reprocesar IA
                                </button>
                                <button type="submit" className="btn-gold flex-1" data-testid="btn-save-factura">
                                    <CheckCircle size={14} weight="bold" /> Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Modal preview PDF/imagen */}
                {previewUrl && (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={closePreview}>
                        <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-5xl w-full h-[90vh] flex flex-col" data-testid="modal-preview">
                            <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--gf-border)] bg-[var(--gf-cream)]">
                                <div className="mono text-sm truncate">{previewName}</div>
                                <div className="flex gap-2">
                                    <a href={previewUrl} download={previewName} className="btn-outline text-xs">Descargar</a>
                                    <button onClick={closePreview} className="btn-outline text-xs">Cerrar ✕</button>
                                </div>
                            </div>
                            <iframe src={previewUrl} title="Factura original" className="flex-1 w-full" />
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
